import { onScopeDispose, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useSound } from '@vueuse/sound';
import { useRouter } from 'vue-router/composables';
import { ReservationsService } from '@/api/services/reservations.service';
import { WsServerEvents } from '@/const/websocket.consts';
import { useAuthStore } from '@/composition/use-auth';
import { useSocket } from '@/composition/use-socket';
import { useUi } from '@/composition/use-ui';
import {
  isRepeatableReservationAlert,
  isReservationAlertTrigger,
  reservationAlertMessageKey,
  reservationAlertWhen,
  TReservationAlertTrigger,
} from '@/domain/reservations/reservation-alerts';
import { getReservationTimeZone } from '@/domain/reservations/reservation-time';
import { PermissionType } from '@/enums/PermissionType';
import { AlertType } from '@/interfaces/IAlert';
import { Globals } from '@/modules/Globals';
import { i18n } from '@/plugins/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useReservationsStore } from '@/stores/reservations.store';

/** Single application-level subscription; listeners are detached from the exact socket that owns them. */
export function useReservationsRealtime() {
  const socket = useSocket();
  const restaurant = useRestaurantStore();
  const store = useReservationsStore();
  const auth = useAuthStore();
  const ui = useUi();
  const router = useRouter();
  const sound = useSound(`${Globals.uploadsPath}/sounds/order.mp3`);
  let disposed = false;
  let alertEpoch = 0;
  let scopeKey = '';
  let detach: () => void = () => undefined;
  const announcements = new Map<string, { epoch: number; pending: boolean }>();
  const canManage = () =>
    auth.permissionSet.has(PermissionType.ReservationsManage);
  const refresh = useDebounceFn(() => {
    if (!disposed) return store.refreshActive();
  }, 400);
  watch(
    [
      socket.socket,
      () => restaurant.restaurant?.id,
      () => restaurant.selectedBranch?.id,
    ],
    () => {
      alertEpoch++;
      detach();
      const rid = restaurant.restaurant?.id;
      const bid = restaurant.selectedBranch?.id;
      const key = `${rid ?? ''}:${bid ?? ''}`;
      if (key !== scopeKey) {
        store.clearForBranch();
        scopeKey = key;
      }
      const current = socket.socket.value;
      if (!current || !rid || !bid) return;
      const ownsScope = () =>
        !disposed &&
        socket.socket.value === current &&
        restaurant.restaurant?.id === rid &&
        restaurant.selectedBranch?.id === bid;
      async function announce(id: number, trigger: TReservationAlertTrigger) {
        const userId = auth.user?.id;
        if (!ownsScope() || !userId || !canManage()) return;
        const key = `${id}:${trigger}`;
        const previous = announcements.get(key);
        if (previous && (!previous.pending || previous.epoch === alertEpoch))
          return;
        const ticket = { epoch: alertEpoch, pending: true };
        announcements.delete(key);
        announcements.set(key, ticket);
        if (announcements.size > 200) {
          const oldest = announcements.keys().next().value;
          if (oldest !== undefined) announcements.delete(oldest);
        }
        const stillAuthorized = () =>
          !disposed &&
          restaurant.restaurant?.id === rid &&
          restaurant.selectedBranch?.id === bid &&
          auth.user?.id === userId &&
          canManage();
        try {
          // The shared socket room is not a reservation authorization boundary.
          const reservation = await ReservationsService.getOne(id);
          if (
            !ownsScope() ||
            !stillAuthorized() ||
            ticket.epoch !== alertEpoch ||
            announcements.get(key) !== ticket ||
            reservation.id !== id ||
            reservation.restaurantId !== rid ||
            reservation.branchId !== bid
          )
            return;
          const messageKey = reservationAlertMessageKey(reservation, trigger);
          if (!messageKey) return;
          const name =
            reservation.guestName ||
            String(i18n.t('reservations.common.anonymous'));
          const tz = getReservationTimeZone(
            restaurant.selectedBranch?.location,
            restaurant.restaurant?.country
          );
          const params =
            messageKey === 'reservations.realtime.guestLate'
              ? { name, minutes: reservation.guestLateMinutes }
              : {
                  name,
                  party: reservation.partySize,
                  time: reservationAlertWhen(
                    reservation,
                    new Date(),
                    tz,
                    restaurant.selectedBranch?.businessDayStartHour,
                    i18n.locale
                  ),
                };
          const message = String(i18n.t(messageKey, params));
          const stringId = restaurant.restaurant?.stringId;
          if (!stringId) return;
          ui.showToast(
            AlertType.Info,
            message,
            {
              text: String(i18n.t('reservations.realtime.view')),
              cb: () => {
                if (!stillAuthorized()) return;
                void router
                  .push({
                    path: `/${stringId}/reservations`,
                    query: {
                      tab: 'today',
                      date: reservation.serviceDate,
                      r: String(id),
                    },
                  })
                  .catch(() => undefined);
              },
            },
            10000
          );
          ticket.pending = false;
          sound.play();
        } catch {
          // Denied, outdated and failed reads must not reveal or announce a row.
        } finally {
          // A repeatable alert only collapses duplicates while its fetch is in flight.
          if (
            announcements.get(key) === ticket &&
            (ticket.pending || isRepeatableReservationAlert(trigger))
          )
            announcements.delete(key);
        }
      }
      const reservationEvent = `r:${rid}:${WsServerEvents.RESERVATION_UPDATED}`;
      const boardEvent = `r:${rid}:${bid}:${WsServerEvents.TABLE_BOARD_DIRTY}`;
      const changed = (payload: unknown) => {
        if (
          !ownsScope() ||
          !payload ||
          typeof payload !== 'object' ||
          !('branchId' in payload) ||
          payload.branchId !== bid ||
          ('restaurantId' in payload && payload.restaurantId !== rid)
        )
          return;
        store.revision += 1;
        void refresh();
        // A create without a trigger includes unpaid creation; never infer an alert.
        if (
          'restaurantId' in payload &&
          payload.restaurantId === rid &&
          'kind' in payload &&
          payload.kind === 'reservation' &&
          'id' in payload &&
          typeof payload.id === 'number' &&
          Number.isInteger(payload.id) &&
          payload.id > 0 &&
          'trigger' in payload &&
          isReservationAlertTrigger(payload.trigger)
        )
          void announce(payload.id, payload.trigger);
      };
      const boardChanged = () => {
        if (!ownsScope()) return;
        store.revision += 1;
        void refresh();
      };
      current.on(reservationEvent, changed);
      current.on(boardEvent, boardChanged);
      detach = () => {
        current.removeListener(reservationEvent, changed);
        current.removeListener(boardEvent, boardChanged);
      };
    },
    { immediate: true, flush: 'sync' }
  );
  watch(
    socket.isConnected,
    (connected) => {
      alertEpoch++;
      if (connected) void refresh();
    },
    { flush: 'sync' }
  );
  watch(
    [() => auth.user?.id, canManage],
    ([userId], [previousUserId]) => {
      alertEpoch++;
      if (userId !== previousUserId) announcements.clear();
    },
    { flush: 'sync' }
  );
  // Settings writes publish the branch update; a stopped module must recover when enabled elsewhere.
  watch(
    () => restaurant.selectedBranch,
    () => {
      void refresh();
    }
  );
  onScopeDispose(() => {
    disposed = true;
    alertEpoch++;
    announcements.clear();
    detach();
  });
}
